import { useState, useEffect, useCallback, Fragment } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Package, Plus, ArrowLeft, Pencil, Trash2, Copy, X, Loader2, Play, Settings2, Clock, Megaphone, Users, Settings, Zap, Save, ChevronDown, ChevronRight, Bot, Image as ImageIcon, MessageSquare, Power, PowerOff, QrCode, Download, FileText } from 'lucide-react';
import ProductImagesTab from '@/components/ProductImagesTab';
import CatalogAttachmentsTab from '@/components/CatalogAttachmentsTab';
import ProductFaqAudiosTab from '@/components/ProductFaqAudiosTab';
import CopyableId from '@/components/CopyableId';
import VendorScheduleModal from '@/components/VendorScheduleModal';
import CampaignPanel from '@/components/CampaignPanel';

const DAYS = [
  { key: 'monday', label: 'Lun', fullLabel: 'Lunes' }, { key: 'tuesday', label: 'Mar', fullLabel: 'Martes' },
  { key: 'wednesday', label: 'Mie', fullLabel: 'Miercoles' }, { key: 'thursday', label: 'Jue', fullLabel: 'Jueves' },
  { key: 'friday', label: 'Vie', fullLabel: 'Viernes' }, { key: 'saturday', label: 'Sab', fullLabel: 'Sabado' },
  { key: 'sunday', label: 'Dom', fullLabel: 'Domingo' },
];

const id8 = (id) => id ? `#${id.substring(0, 8)}` : '';

const DAY_COLORS = {
  monday: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  tuesday: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  wednesday: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
  thursday: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  friday: 'bg-pink-500/15 text-pink-300 border-pink-500/30',
  saturday: 'bg-teal-500/15 text-teal-300 border-teal-500/30',
  sunday: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
};

function CoverageCalendar({ group, getVendorName, testPrefix = 'cov' }) {
  const [open, setOpen] = useState(false);
  if (!group || !(group.vendor_ids || []).length) return null;

  const getSlot = (vid, dayKey) => {
    const vs = (group.schedule || {})[vid];
    if (!vs || Object.keys(vs).length === 0) return { noSchedule: true };
    const d = vs[dayKey];
    if (!d || d.active === false) return null;
    const sh = d.start_hour;
    const eh = d.end_hour;
    if (sh == null || eh == null || (sh === 0 && eh === 24)) return { allDay: true };
    return { allDay: false, start: `${String(sh).padStart(2, '0')}:00`, end: `${String(eh).padStart(2, '0')}:00` };
  };

  const slotsByDay = {};
  let totalAssignments = 0;
  const uniqueVendors = new Set();
  DAYS.forEach(d => {
    const list = [];
    (group.vendor_ids || []).forEach(vid => {
      const slot = getSlot(vid, d.key);
      if (slot) { list.push({ vid, ...slot }); totalAssignments++; uniqueVendors.add(vid); }
    });
    slotsByDay[d.key] = list;
  });
  const daysWithAssignments = DAYS.filter(d => slotsByDay[d.key].length > 0).length;

  // Gaps in business hours (8-20) per day
  const alerts = [];
  DAYS.forEach(d => {
    const covered = new Array(24).fill(false);
    (group.vendor_ids || []).forEach(vid => {
      const vs = (group.schedule || {})[vid] || {};
      const dc = vs[d.key]; if (!dc || dc.active === false) return;
      const sh = dc.start_hour ?? 0; const eh = dc.end_hour ?? 24;
      for (let h = sh; h < eh; h++) covered[h] = true;
    });
    const gaps = [];
    for (let h = 8; h < 20; h++) if (!covered[h]) gaps.push(h);
    if (gaps.length === 12) alerts.push(`${d.label}: sin cobertura total`);
    else if (gaps.length > 0) {
      const ranges = []; let start = gaps[0];
      for (let i = 1; i <= gaps.length; i++) {
        if (i === gaps.length || gaps[i] !== gaps[i - 1] + 1) {
          ranges.push(`${String(start).padStart(2, '0')}:00-${String(gaps[i - 1] + 1).padStart(2, '0')}:00`);
          if (i < gaps.length) start = gaps[i];
        }
      }
      alerts.push(`${d.label}: sin cobertura ${ranges.join(', ')}`);
    }
  });

  return (
    <div className="mt-3 border-t border-white/5 pt-3" data-testid={`${testPrefix}-calendar-section-${group.id}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-[10px] text-muted-foreground hover:text-white flex items-center gap-1 transition-colors"
        data-testid={`${testPrefix}-calendar-toggle-${group.id}`}
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Ver cobertura semanal
      </button>
      {open && (
        <div className="mt-3 space-y-3" data-testid={`${testPrefix}-calendar-${group.id}`}>
          <div className="grid grid-cols-7 gap-1.5">
            {DAYS.map(d => {
              const slots = slotsByDay[d.key];
              return (
                <div key={d.key} className="space-y-1">
                  <div className={`text-center text-[9px] font-semibold py-1 rounded-md border ${DAY_COLORS[d.key]}`}>{d.label}</div>
                  <div className="space-y-1 min-h-[48px]">
                    {slots.length === 0 ? (
                      <p className="text-[8px] text-muted-foreground italic text-center pt-1">Sin asignaciones</p>
                    ) : slots.map(s => (
                      <div key={s.vid} className={`rounded px-1.5 py-1 ${s.noSchedule ? 'bg-white/[0.02] border border-white/10' : s.allDay ? 'bg-emerald-500/10 border border-emerald-500/25' : 'bg-white/[0.04] border border-white/10'}`}>
                        <p className="text-[9px] text-white font-medium truncate" title={getVendorName(s.vid)}>{getVendorName(s.vid)}</p>
                        {s.noSchedule ? (
                          <span className="inline-block mt-0.5 px-1 py-px rounded-sm text-[7px] bg-white/5 text-muted-foreground border border-white/10">Sin horario</span>
                        ) : s.allDay ? (
                          <span className="inline-block mt-0.5 px-1 py-px rounded-sm text-[7px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">Todo el dia</span>
                        ) : (
                          <p className="text-[8px] text-muted-foreground mt-0.5">{s.start}–{s.end}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <div className="rounded-md bg-black/30 border border-white/5 px-2 py-1.5">
              <p className="text-[8px] text-muted-foreground">Asignaciones</p>
              <p className="text-xs text-white font-semibold">{totalAssignments}</p>
            </div>
            <div className="rounded-md bg-black/30 border border-white/5 px-2 py-1.5">
              <p className="text-[8px] text-muted-foreground">Dias activos</p>
              <p className="text-xs text-white font-semibold">{daysWithAssignments}/7</p>
            </div>
            <div className="rounded-md bg-black/30 border border-white/5 px-2 py-1.5">
              <p className="text-[8px] text-muted-foreground">Vendedores</p>
              <p className="text-xs text-white font-semibold">{uniqueVendors.size}</p>
            </div>
          </div>
          {alerts.length > 0 && (
            <div className="space-y-0.5 pt-1 border-t border-white/5">
              {alerts.map((a, i) => <p key={i} className="text-[8px] text-amber-400">⚠ {a}</p>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProductsPage({ embedded = false }) {
  const { user, impersonating } = useAuth();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDialog, setProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productStep, setProductStep] = useState(1);
  const [createdProduct, setCreatedProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '', description: '', brand_id: '', is_active: true,
    kind: 'product', category: '', ai_description: '', price: '', currency: 'MXN',
  });

  // Estado para modal de cambio de estado (deactivate/reactivate)
  const [statusDialog, setStatusDialog] = useState(false);
  const [statusProduct, setStatusProduct] = useState(null);
  const [statusAction, setStatusAction] = useState('deactivate');
  const [statusReason, setStatusReason] = useState('');
  const [statusError, setStatusError] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);

  // Permisos derivados del user
  const isSuperAdmin = user?.role === 'superadmin'
    || user?.platform_role === 'superadmin'
    || impersonating === true;
  const canManage = user?.role !== 'vendor';
  const btype = user?.business_type || user?.company?.business_type || 'agency';
  const industry = user?.industry || user?.company?.industry || 'real_estate';
  const isVendor = user?.role === 'vendor';
  const isGuard = user?.role === 'guard';
  const esCatalogo = ['education', 'generic', 'comercial', 'retail'].includes((industry || '').toLowerCase())
    || ['education', 'generic', 'retail'].includes((btype || '').toLowerCase());
  const viveEnTeamConfig = !isVendor && !isGuard && btype !== 'independent'
    && industry !== 'car_dealer' && industry !== 'automotive' && btype !== 'car_dealer'
    && btype !== 'developer'
    && !esCatalogo;

  const loadAll = useCallback(async () => {
    try {
      const [pr, br, tm] = await Promise.all([
        api.get('/products'), api.get('/brands').catch(() => ({ data: [] })), api.get('/team')
      ]);
      setProducts(pr.data); setBrands(br.data); setTeam(tm.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  const getBrandName = (id) => brands.find(b => b.id === id)?.name || '';

  const closeProductDialog = () => {
    setProductDialog(false); setEditingProduct(null); setProductStep(1); setCreatedProduct(null);
    setProductForm({
      name: '', description: '', brand_id: '', is_active: true,
      kind: 'product', category: '', ai_description: '', price: '', currency: 'MXN',
    }); loadAll();
  };
  const saveProduct = async () => {
    try {
      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, productForm);
        toast.success('Actualizado'); closeProductDialog();
      } else {
        const r = await api.post('/products', productForm);
        toast.success('Creado');
        if (esCatalogo && r?.data?.id) { setCreatedProduct(r.data); setProductStep(2); loadAll(); }
        else { closeProductDialog(); }
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openStatusDialog = (product, action) => {
    setStatusProduct(product);
    setStatusAction(action);
    setStatusReason('');
    setStatusError('');
    setStatusDialog(true);
  };

  const submitStatus = async () => {
    if (!statusProduct) return;
    if (statusReason.trim().length < 10) return;
    setSavingStatus(true);
    setStatusError('');
    try {
      await api.patch(`/products/${statusProduct.id}/status`, {
        action: statusAction,
        reason: statusReason.trim(),
      });
      toast.success(statusAction === 'deactivate' ? 'Producto desactivado' : 'Producto reactivado');
      setStatusDialog(false);
      setStatusProduct(null);
      setStatusReason('');
      loadAll();
    } catch (e) {
      setStatusError(e.response?.data?.detail || 'Error al cambiar el estado del producto');
    } finally {
      setSavingStatus(false);
    }
  };

  const visibleProducts = isSuperAdmin
    ? products
    : products.filter(p => p.is_active !== false);

  if (!embedded && viveEnTeamConfig) {
    return <Navigate to="/team-config?tab=productos" replace />;
  }

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (selectedProduct) return <ProductDetail product={selectedProduct} brands={brands} team={team} getBrandName={getBrandName} onBack={() => { setSelectedProduct(null); loadAll(); }} />;

  return (
    <div className={embedded ? 'space-y-4' : 'space-y-6'} data-testid="products-page">
      <div className="flex items-center justify-between">
        <div>
          {!embedded && (
            <h1 className="text-xl font-bold text-white flex items-center gap-2"><Package className="w-5 h-5 text-emerald-400" />{esCatalogo ? 'Productos y Servicios' : 'Productos'}</h1>
          )}
          <p className="text-xs text-muted-foreground mt-1">{visibleProducts.length} productos {isSuperAdmin ? 'configurados' : 'activos'}</p>
        </div>
        {canManage && (
          <Button size="sm" className="text-xs" onClick={() => { setEditingProduct(null); setProductStep(1); setCreatedProduct(null); setProductForm({ name: '', description: '', brand_id: '', is_active: true, kind: 'product', category: '', ai_description: '', price: '', currency: 'MXN' }); setProductDialog(true); }} data-testid="new-product-btn"><Plus className="w-3.5 h-3.5 mr-1" />{esCatalogo ? 'Nuevo producto o servicio' : 'Nuevo Producto'}</Button>
        )}
      </div>
      {visibleProducts.length === 0 ? (
        <Card className="bg-card/50 backdrop-blur-md border-white/5"><CardContent className="py-12 text-center space-y-3">
          <Package className="w-10 h-10 text-muted-foreground mx-auto" />
          <p className="text-sm text-white">Aun no tienes productos configurados.</p>
          <p className="text-xs text-muted-foreground">Crea tu primer producto para empezar a recibir leads automaticamente.</p>
          {canManage && (
            <Button size="sm" className="text-xs" onClick={() => setProductDialog(true)}>Crear primer producto</Button>
          )}
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {visibleProducts.map(p => (
            <Card key={p.id} className="bg-card/50 backdrop-blur-md border-white/5 hover:border-white/10 transition-all cursor-pointer" data-testid={`product-card-${p.id}`} onClick={() => setSelectedProduct(p)}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-white">{p.name}</p>
                    <Badge
                      variant="outline"
                      className={`text-[9px] ${p.is_active === false ? 'text-red-400 border-red-500/30 bg-red-500/5' : 'text-[#A5D62C] border-[#A5D62C]/30 bg-[#A5D62C]/5'}`}
                      data-testid={`product-status-badge-${p.id}`}
                    >
                      {p.is_active === false ? 'Inactivo' : 'Activo'}
                    </Badge>
                    {esCatalogo && (
                      <Badge variant="outline" className="text-[9px] text-sky-300 border-sky-500/30">
                        {p.kind === 'service' ? 'Servicio' : 'Producto'}
                      </Badge>
                    )}
                  </div>
                  <CopyableId id={p.id} />
                  <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                    {p.brand_id && <span>Marca: {getBrandName(p.brand_id)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canManage && (
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => { e.stopPropagation(); setEditingProduct(p); setProductForm({ name: p.name, description: p.description || '', brand_id: p.brand_id || '', is_active: p.is_active, kind: p.kind || 'product', category: p.category || '', ai_description: p.ai_description || '', price: p.price ?? '', currency: p.currency || 'MXN' }); setProductDialog(true); }} data-testid={`edit-product-${p.id}`}><Pencil className="w-3 h-3" /></Button>
                  )}
                  {isSuperAdmin && p.is_active !== false && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-400 hover:bg-red-500/10"
                      onClick={e => { e.stopPropagation(); openStatusDialog(p, 'deactivate'); }}
                      title="Desactivar producto"
                      data-testid={`deactivate-product-${p.id}`}
                    >
                      <PowerOff className="w-3 h-3" />
                    </Button>
                  )}
                  {isSuperAdmin && p.is_active === false && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-[#A5D62C] hover:bg-[#A5D62C]/10"
                      onClick={e => { e.stopPropagation(); openStatusDialog(p, 'reactivate'); }}
                      title="Reactivar producto"
                      data-testid={`reactivate-product-${p.id}`}
                    >
                      <Power className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={productDialog} onOpenChange={(v) => { if (!v) closeProductDialog(); else setProductDialog(true); }}>
        <DialogContent className="bg-[#0f1117] border-white/10 text-white max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-sm">{editingProduct ? 'Editar' : (productStep === 2 ? 'Material y FAQ' : 'Nuevo')} {esCatalogo ? 'producto o servicio' : 'Producto'}</DialogTitle></DialogHeader>
          {productStep === 1 && (
          <div className="space-y-3">
            <div><Label className="text-xs">Nombre *</Label><Input value={productForm.name} onChange={e => setProductForm({...productForm, name: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" data-testid="pp-name" /></div>
            {esCatalogo && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Tipo</Label>
                  <Select value={productForm.kind || 'product'} onValueChange={v => setProductForm({...productForm, kind: v})}>
                    <SelectTrigger className="bg-black/20 border-white/10 text-xs mt-1" data-testid="pp-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="product">Producto</SelectItem>
                      <SelectItem value="service">Servicio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Categoría</Label><Input value={productForm.category} onChange={e => setProductForm({...productForm, category: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="Curso, membresía..." /></div>
              </div>
            )}
            <div><Label className="text-xs">Descripción comercial</Label><Input value={productForm.description} onChange={e => setProductForm({...productForm, description: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" /></div>
            {esCatalogo && (
              <div>
                <Label className="text-xs">Descripción para la IA</Label>
                <Textarea value={productForm.ai_description} onChange={e => setProductForm({...productForm, ai_description: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1 min-h-[80px]" placeholder="Qué debe saber la IA para contestar bien: incluye, no incluye, para quién es, cómo se vende." data-testid="pp-ai-description" />
              </div>
            )}
            {esCatalogo && (
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Precio</Label><Input type="number" value={productForm.price} onChange={e => setProductForm({...productForm, price: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" /></div>
                <div><Label className="text-xs">Moneda</Label><Input value={productForm.currency} onChange={e => setProductForm({...productForm, currency: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" /></div>
              </div>
            )}
            {brands.length > 0 && (<div><Label className="text-xs">Marca</Label><Select value={productForm.brand_id || 'none'} onValueChange={v => setProductForm({...productForm, brand_id: v === 'none' ? '' : v})}><SelectTrigger className="bg-black/20 border-white/10 text-xs mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Sin marca</SelectItem>{brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select></div>)}
            <div className="flex items-center gap-2"><Switch checked={productForm.is_active} onCheckedChange={v => setProductForm({...productForm, is_active: v})} /><Label className="text-xs">Activo</Label></div>
            <Button size="sm" className="w-full text-xs" onClick={saveProduct} data-testid="pp-save">{esCatalogo && !editingProduct ? 'Guardar y agregar material' : 'Guardar'}</Button>
          </div>
          )}
          {productStep === 2 && createdProduct && (
          <div className="space-y-4">
            <p className="text-[11px] text-emerald-300">✓ Producto creado. Agrega imágenes, videos/PDF/links, FAQ y audios — todo es fuente de conocimiento de la IA.</p>
            <ProductImagesTab productId={createdProduct.id} initialImages={createdProduct.images || []} />
            <CatalogAttachmentsTab product={createdProduct} />
            <ProductFaqAudiosTab productId={createdProduct.id} initialFaqs={createdProduct.faqs || []} initialAudios={createdProduct.audios || []} />
            <Button size="sm" className="w-full text-xs" onClick={closeProductDialog} data-testid="pp-step2-done">Listo</Button>
          </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de cambio de estado (deactivate/reactivate) */}
      <Dialog
        open={statusDialog}
        onOpenChange={(open) => { if (!savingStatus) { setStatusDialog(open); if (!open) { setStatusError(''); setStatusReason(''); } } }}
      >
        <DialogContent className="bg-[#0f1117] border-white/10 text-white max-w-sm" data-testid="status-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">
              {statusAction === 'deactivate' ? 'Desactivar producto' : 'Reactivar producto'}
            </DialogTitle>
            {statusProduct && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="status-dialog-product-name">{statusProduct.name}</p>
            )}
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Motivo <span className="text-red-400">*</span></Label>
              <Textarea
                value={statusReason}
                onChange={e => { setStatusReason(e.target.value); if (statusError) setStatusError(''); }}
                placeholder="Describe el motivo (mínimo 10 caracteres)"
                rows={4}
                className="bg-black/20 border-white/10 text-xs mt-1"
                data-testid="status-reason-input"
                disabled={savingStatus}
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                {statusReason.trim().length}/10 caracteres mínimos
              </p>
            </div>
            {statusError && (
              <p className="text-xs text-red-400" data-testid="status-error">{statusError}</p>
            )}
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={() => { setStatusDialog(false); setStatusError(''); setStatusReason(''); }}
                disabled={savingStatus}
                data-testid="status-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                className={`text-xs ${statusAction === 'deactivate' ? 'bg-red-500 hover:bg-red-500/90 text-white' : 'bg-[#A5D62C] hover:bg-[#A5D62C]/90 text-black'}`}
                onClick={submitStatus}
                disabled={savingStatus || statusReason.trim().length < 10}
                data-testid="status-confirm-btn"
              >
                {savingStatus ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                Confirmar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProductDetail({ product, brands, team, getBrandName, onBack }) {
  const [campaigns, setCampaigns] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [campaignDialog, setCampaignDialog] = useState(false);
  const [campForm, setCampForm] = useState({ name: '', medium: 'digital', channel: '', link_type: 'form', whatsapp_number: '', whatsapp_message: '' });
  const [resolvedWaba, setResolvedWaba] = useState(null); // { phone_number, provider, waba_id } o null
  const [resolvingWaba, setResolvingWaba] = useState(false);
  const [campaignChannels, setCampaignChannels] = useState({ digital: [], print: [] });
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [scheduleVendor, setScheduleVendor] = useState(null);
  const [scheduleGroup, setScheduleGroup] = useState(null);
  const [scheduleGroupId, setScheduleGroupId] = useState('');
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [simForm, setSimForm] = useState({ lead_source_id: '' });
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [expandedCamps, setExpandedCamps] = useState({});

  const webhookBase = `${window.location.origin}/api/webhook/source/`;
  const getVendorName = (vid) => team.find(t => t.id === vid)?.name || vid;

  const loadData = useCallback(async () => {
    try {
      // R5: se fue la llamada a /roulette/config — servía SOLO al toggle jubilado
      // "Leads fuera de horario" (out_of_hours_assignee).
      const [u, c, s, g, cch] = await Promise.all([
        api.get(`/products/${product.id}/users`),
        api.get(`/campaigns?product_id=${product.id}`).catch(() => ({ data: [] })),
        api.get('/lead-sources').catch(() => ({ data: [] })),
        api.get('/roulette/groups').catch(() => ({ data: [] })),
        api.get('/campaigns/channels').catch(() => ({ data: { digital: [], print: [] } })),
      ]);
      setAssignedUsers(u.data.assigned || []); setAvailableUsers(u.data.available || []);
      setCampaigns(c.data || []);
      setLeadSources((s.data || []).filter(src => src.product_id === product.id));
      setGroups(Array.isArray(g.data) ? g.data : []);
      setCampaignChannels(cch.data || { digital: [], print: [] });
    } catch {}
  }, [product.id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-resolución de WABA cuando el canal es WhatsApp.
  // GET /campaigns/resolve-waba?product_id={id} → si trae phone_number,
  // bloqueamos el input y prellenamos. Si no, dejamos editable manual.
  useEffect(() => {
    let cancelled = false;
    if (!campaignDialog || campForm.link_type !== 'whatsapp' || !product?.id) {
      setResolvedWaba(null);
      return;
    }
    setResolvingWaba(true);
    api.get('/campaigns/resolve-waba', { params: { product_id: product.id } })
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.found) {
          setResolvedWaba(data);
        } else {
          setResolvedWaba(null);
        }
      })
      .catch(() => { if (!cancelled) setResolvedWaba(null); })
      .finally(() => { if (!cancelled) setResolvingWaba(false); });
    return () => { cancelled = true; };
  }, [campaignDialog, campForm.link_type, product?.id]);

  const assignUser = async (userId) => {
    try {
      await api.post(`/products/${product.id}/users`, { user_id: userId });
      // Sincronizar con TODOS los grupos activos vinculados al producto
      const relatedGroups = (groups || []).filter(g => g.is_active && (
        (g.type === 'product' && g.linked_id === product.id) ||
        (g.type === 'campaign' && (leadSources || []).some(s => s.id === g.linked_id && s.product_id === product.id)) ||
        (g.type === 'brand' && product.brand_id && g.linked_id === product.brand_id)
      ));
      for (const g of relatedGroups) {
        const updated = [...new Set([...(g.vendor_ids || []), userId])];
        try { await api.put(`/roulette/groups/${g.id}`, { vendor_ids: updated }); } catch {}
      }
      toast.success('Asignado');
      await loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const unassignUser = async (userId) => {
    try {
      await api.delete(`/products/${product.id}/users/${userId}`);
      // Sincronizar inversamente con TODOS los grupos activos del producto
      const relatedGroups = (groups || []).filter(g => g.is_active && (
        (g.type === 'product' && g.linked_id === product.id) ||
        (g.type === 'campaign' && (leadSources || []).some(s => s.id === g.linked_id && s.product_id === product.id)) ||
        (g.type === 'brand' && product.brand_id && g.linked_id === product.brand_id)
      ));
      for (const g of relatedGroups) {
        const updated = (g.vendor_ids || []).filter(id => id !== userId);
        try { await api.put(`/roulette/groups/${g.id}`, { vendor_ids: updated }); } catch {}
      }
      toast.success('Removido');
      await loadData();
    } catch { toast.error('Error'); }
  };

  const createCampaign = async () => {
    if (!campForm.name) { toast.error('Nombre requerido'); return; }
    if (!campForm.channel) { toast.error('Selecciona un canal'); return; }
    if (campForm.link_type === 'whatsapp' && !resolvedWaba?.phone_number) {
      toast.error('Este producto no tiene WhatsApp configurado para campañas.');
      return;
    }
    try {
      const payload = { ...campForm, product_id: product.id };
      // Si el WABA viene resuelto, dejamos que el backend complete el número.
      if (resolvedWaba?.phone_number) {
        delete payload.whatsapp_number;
      }
      await api.post('/campaigns', payload);
      toast.success('Campana creada'); setCampaignDialog(false);
      setCampForm({ name: '', medium: 'digital', channel: '', link_type: 'form', whatsapp_number: '', whatsapp_message: '' }); loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const deleteCampaign = async (id) => { if (!window.confirm('¿Eliminar?')) return; await api.delete(`/campaigns/${id}`); toast.success('Eliminada'); loadData(); };

  const changeRoleLevel = async (sourceId, newLevel) => {
    try { await api.put(`/lead-sources/${sourceId}`, { role_level: newLevel }); loadData(); } catch { toast.error('Error'); }
  };

  const productGroup = groups.find(g => g.type === 'product' && g.linked_id === product.id);
  const getCampaignGroup = (sourceId) => groups.find(g => g.type === 'campaign' && g.linked_id === sourceId);

  const createProductGroup = async () => {
    try { await api.post('/roulette/groups', { name: `Grupo ${product.name}`, type: 'product', linked_id: product.id, vendor_ids: assignedUsers.map(u => u.id) }); toast.success('Grupo creado'); loadData(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const createCampaignGroup = async (source) => {
    try { await api.post('/roulette/groups', { name: `Grupo ${source.name}`, type: 'campaign', linked_id: source.id, vendor_ids: [] }); toast.success('Grupo creado'); loadData(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const addVendorToCampaignGroup = async (group, userId) => {
    try {
      const updated = [...new Set([...(group.vendor_ids || []), userId])];
      await api.put(`/roulette/groups/${group.id}`, { vendor_ids: updated });
      toast.success('Vendedor agregado a la campana');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const removeVendorFromCampaignGroup = async (group, userId) => {
    try {
      const updated = (group.vendor_ids || []).filter(id => id !== userId);
      await api.put(`/roulette/groups/${group.id}`, { vendor_ids: updated });
      toast.success('Vendedor removido');
      loadData();
    } catch { toast.error('Error'); }
  };

  const openSchedule = (vendorId, group) => {
    if (!group || !group.id) { toast.error('Grupo no valido'); return; }
    setScheduleVendor(vendorId);
    setScheduleGroup(group);
    setScheduleGroupId(group.id);
    setScheduleDialog(true);
  };
  const saveSchedule = async (newVendorSchedule) => {
    // Look up the latest group from state to avoid stale refs
    const liveGroup = groups.find(g => g.id === scheduleGroupId) || scheduleGroup;
    if (!liveGroup || !liveGroup.id) { toast.error('Grupo no encontrado'); return; }
    const schedule = { ...(liveGroup.schedule || {}) };
    schedule[scheduleVendor] = newVendorSchedule;
    setScheduleSaving(true);
    try {
      await api.put(`/roulette/groups/${liveGroup.id}`, { schedule });
      toast.success('Horarios guardados');
      setScheduleDialog(false);
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar horarios');
    } finally {
      setScheduleSaving(false);
    }
  };

  const runSim = async () => {
    setSimLoading(true);
    try { const r = await api.post('/roulette/simulate', { lead_source_id: simForm.lead_source_id, product_id: product.id, brand_id: product.brand_id || '' }); setSimResult(r.data); } catch { toast.error('Error'); } finally { setSimLoading(false); }
  };

  const renderGroupVendors = (group) => {
    if (!group) return null;
    return (
      <div className="space-y-1.5 mt-2">
        {(group.vendor_ids || []).map(vid => {
          const vs = (group.schedule || {})[vid] || {};
          const activeDays = DAYS.filter(d => vs[d.key]?.active);
          const txt = activeDays.length > 0 ? `${activeDays.map(d => d.label).join(', ')} ${vs[activeDays[0]?.key]?.start_hour ?? '?'}:00-${vs[activeDays[0]?.key]?.end_hour ?? '?'}:00` : 'Sin horario (recibe siempre)';
          return (<div key={vid} className="flex items-center justify-between px-3 py-2 rounded bg-white/[0.03] border border-white/5"><div><p className="text-xs text-white">{getVendorName(vid)}</p><p className="text-[9px] text-muted-foreground">{txt}</p></div><Button variant="ghost" size="sm" className="h-6 text-[9px] text-sky-400" onClick={() => openSchedule(vid, group)} data-testid={`schedule-${vid}`}><Clock className="w-3 h-3 mr-1" />Horarios</Button></div>);
        })}
        {(group.vendor_ids || []).length === 0 && <p className="text-[10px] text-muted-foreground italic">Sin vendedores en este grupo</p>}
      </div>
    );
  };

  const channels = campaignChannels[campForm.medium] || [];

  return (
    <div className="space-y-6" data-testid="product-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={onBack} data-testid="back-to-list"><ArrowLeft className="w-3.5 h-3.5 mr-1" />Productos</Button>
      </div>
      <div>
        <h1 className="text-xl font-bold text-white">{product.name}</h1>
        <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
          <span>{id8(product.id)}</span>
          {product.brand_id && <span>Marca: {getBrandName(product.brand_id)}</span>}
          <span>{campaigns.length} campanas</span>
          <span>{assignedUsers.length} vendedores</span>
          <Badge variant="outline" className={`text-[9px] ${product.is_active ? 'text-emerald-400 border-emerald-500/20' : 'text-muted-foreground border-white/10'}`}>{product.is_active ? 'Activo' : 'Inactivo'}</Badge>
        </div>
      </div>

      <Tabs defaultValue="images" className="w-full">
        <TabsList className="bg-black/20 border border-white/5 w-full justify-start">
          <TabsTrigger value="images" className="text-xs gap-1.5 data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400" data-testid="tab-images"><ImageIcon className="w-3.5 h-3.5" />Imágenes</TabsTrigger>
          <TabsTrigger value="material" className="text-xs gap-1.5 data-[state=active]:bg-pink-500/20 data-[state=active]:text-pink-400" data-testid="tab-material"><FileText className="w-3.5 h-3.5" />Videos / PDF / Links</TabsTrigger>
          <TabsTrigger value="campaigns" className="text-xs gap-1.5 data-[state=active]:bg-sky-500/20 data-[state=active]:text-sky-400" data-testid="tab-campaigns"><Megaphone className="w-3.5 h-3.5" />Campanas</TabsTrigger>
        </TabsList>

        {/* TAB 1: CAMPANAS */}
        <TabsContent value="images" className="space-y-4">
          <ProductImagesTab productId={product.id} initialImages={product.images || []} />
        </TabsContent>

        <TabsContent value="material" className="space-y-4">
          <CatalogAttachmentsTab product={product} />
        </TabsContent>

        <TabsContent value="campaigns" className="space-y-4" data-testid="tab-campaigns-content">
          <CampaignPanel scopeType="product" scopeId={product.id} scopeName={product.name} scopeLabel="producto" assignedUsers={assignedUsers} />
        </TabsContent>

        {/* TAB 5: ASISTENTE IA */}
      </Tabs>




      {/* Schedule Dialog (reutiliza VendorScheduleModal) */}
      <VendorScheduleModal
        open={scheduleDialog}
        onOpenChange={setScheduleDialog}
        vendorName={getVendorName(scheduleVendor)}
        schedule={((groups.find(g => g.id === scheduleGroupId) || scheduleGroup)?.schedule || {})[scheduleVendor] || {}}
        onSave={saveSchedule}
        saving={scheduleSaving}
      />
    </div>
  );
}
